import React, {useState, useEffect} from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Grid,
  Alert,
  Tooltip,
  Skeleton,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon,
  Assessment as ReportIcon,
  VpnKey as ApiIcon,
} from '@mui/icons-material';
import jwtAxios from '@crema/services/auth/jwt-auth';
import { formatDateToJerusalem } from '../../../utils/dateFormatter';
import {useNavigate, useParams} from 'react-router-dom';

const DailyReportDetails = () => {
  const navigate = useNavigate();
  const {reportId} = useParams();
  const [reportDetails, setReportDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (reportId) {
      fetchReportDetails();
    }
  }, [reportId]);

  const fetchReportDetails = async () => {
    try {
      setLoading(true);
      const response = await jwtAxios.get(`/daily-reports/${reportId}`);

      if (response.data.status === 200) {
        setReportDetails(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch report details');
      }
    } catch (err) {
      console.error('Error fetching report details:', err);
      setError('Failed to fetch report details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCombinedReport = async () => {
    try {
      const response = await jwtAxios.get(
        `/daily-reports/${reportId}/download`,
        {
          responseType: 'blob',
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `daily_report_${reportDetails?.[0]?.report_date}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading combined report:', err);
      setError('Failed to download combined report');
    }
  };

  const handleDownloadApiReport = async (apiId, fileName) => {
    try {
      const response = await jwtAxios.get(
        `/daily-reports/${reportId}/api/${apiId}/download`,
        {
          responseType: 'blob',
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading API report:', err);
      setError('Failed to download API report');
    }
  };

  const SkeletonTable = () => (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
      >
        <Skeleton variant='text' width={300} height={40} />
        <Skeleton variant='rectangular' width={200} height={40} />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {Array.from({length: 7}).map((_, index) => (
                <TableCell key={index}>
                  <Skeleton variant='text' width={100} height={24} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({length: 5}).map((_, index) => (
              <TableRow key={index}>
                {Array.from({length: 7}).map((_, cellIndex) => (
                  <TableCell key={cellIndex}>
                    <Skeleton variant='text' width={80} height={24} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  if (loading) {
    return (
      <Box>
        <Box display='flex' alignItems='center' mb={3}>
          <Skeleton variant='text' width={200} height={40} />
        </Box>
        <SkeletonTable />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box display='flex' alignItems='center' mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/Reports/DailyReports')}
            sx={{mr: 2}}
          >
            Back to Reports
          </Button>
        </Box>
        <Alert severity='error'>{error}</Alert>
      </Box>
    );
  }

  if (!reportDetails || reportDetails.length === 0) {
    return (
      <Box>
        <Box display='flex' alignItems='center' mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/Reports/DailyReports')}
            sx={{mr: 2}}
          >
            Back to Reports
          </Button>
        </Box>
        <Alert severity='info'>Report not found</Alert>
      </Box>
    );
  }

  const mainReport = reportDetails[0];
  const apiDetails = reportDetails.filter((detail) => detail.api_detail_id);

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{mb: 3}}>
        <Link
          component='button'
          variant='body1'
          onClick={() => navigate('/Reports/DailyReports')}
          sx={{textDecoration: 'none'}}
        >
          Daily Reports
        </Link>
        <Typography color='text.primary'>
          {formatDateToJerusalem(mainReport.report_date, 'date-only')}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
      >
        <Box display='flex' alignItems='center'>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/Reports/DailyReports')}
            sx={{mr: 2}}
          >
            Back
          </Button>
          <Box>
            <Typography variant='h4' display='flex' alignItems='center'>
              <ReportIcon sx={{mr: 1}} />
              Daily Report -{' '}
              {formatDateToJerusalem(mainReport.report_date, 'date-only')}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Generated on {formatDateToJerusalem(mainReport.created_at)}
            </Typography>
          </Box>
        </Box>
        <Button
          variant='contained'
          startIcon={<FileDownloadIcon />}
          onClick={handleDownloadCombinedReport}
        >
          Download Combined Report
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{mb: 3}}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant='h6' color='primary'>
                {mainReport.total_leads}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Total Leads
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant='h6' color='success.main'>
                {mainReport.main_valid_leads || mainReport.valid_leads}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Valid Leads
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant='h6' color='error.main'>
                {mainReport.main_invalid_leads || mainReport.invalid_leads}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Invalid Leads
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant='h6'>
                {mainReport.total_leads > 0
                  ? (
                      ((mainReport.main_valid_leads || mainReport.valid_leads) /
                        mainReport.total_leads) *
                      100
                    ).toFixed(1)
                  : 0}
                %
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Success Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant='h6' color='warning.main'>
                {mainReport.duplicated_free_data || 0}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Dup - Free
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant='h6' color='error.main'>
                {mainReport.duplicated_sent_data || 0}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Dup - Sent
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* API Details Table */}
      <Card>
        <CardContent>
          <Typography variant='h6' sx={{mb: 2}}>
            API Reports ({apiDetails.length})
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>API Name</TableCell>
                  <TableCell>Integration Source</TableCell>
                  <TableCell>Total Leads</TableCell>
                  <TableCell>Valid Leads</TableCell>
                  <TableCell>Invalid Leads</TableCell>
                  <TableCell>Dup - Free</TableCell>
                  <TableCell>Dup - Sent</TableCell>
                  <TableCell>Success Rate</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiDetails.map((api) => (
                  <TableRow key={api.api_detail_id}>
                    <TableCell>
                      <Box display='flex' alignItems='center'>
                        <ApiIcon sx={{mr: 1, color: 'text.secondary'}} />
                        <Typography variant='body2' fontWeight='medium'>
                          {api.api_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={api.integration_source || 'N/A'}
                        color='primary'
                        size='small'
                        variant='outlined'
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2' fontWeight='medium'>
                        {api.leads_count}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={api.valid_leads}
                        color='success'
                        size='small'
                        variant='outlined'
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={api.invalid_leads}
                        color='error'
                        size='small'
                        variant='outlined'
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant='body2'
                        color='warning.main'
                        fontWeight='bold'
                      >
                        {api.duplicated_free_data || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant='body2'
                        color='error.main'
                        fontWeight='bold'
                      >
                        {api.duplicated_sent_data || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        {api.leads_count > 0
                          ? ((api.valid_leads / api.leads_count) * 100).toFixed(
                              1,
                            )
                          : 0}
                        %
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title='Download API Report'>
                        <IconButton
                          size='small'
                          onClick={() =>
                            handleDownloadApiReport(
                              api.api_credential_id,
                              `${api.api_name}_${mainReport.report_date}.xlsx`,
                            )
                          }
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {apiDetails.length === 0 && (
            <Box textAlign='center' py={4}>
              <Typography variant='h6' color='text.secondary'>
                No API reports found
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{mt: 1}}>
                This report may not have any API data for the selected date
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DailyReportDetails;
